import { Button, Checkbox, Modal } from "@devhop/ui";
import {
  CheckCircle2Icon,
  Loader2,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { FileUploadItemStatus } from "@/shared/ui/PresignedImageUploadTrigger";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import type { ProductImageDTO } from "../../domain/contracts";
import {
  useBulkDeleteProductImages,
  useCreateProductImagesBulk,
  useDeleteProductImage,
  useProductImagesQuery,
  useReorderProductImages,
} from "../api/queries";

type PendingUploadItem = {
  file: File;
  status: FileUploadItemStatus | "saving";
  key?: string;
  objectUrl: string;
};

interface ProductImagesSectionProps {
  productId: string;
}

export function ProductImagesSection({ productId }: ProductImagesSectionProps) {
  const { data: images = [], isLoading } = useProductImagesQuery(productId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<ProductImageDTO | null>(
    null,
  );
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingUploads, setPendingUploads] = useState<PendingUploadItem[]>([]);
  const pendingUploadsRef = useRef(pendingUploads);
  pendingUploadsRef.current = pendingUploads;

  useEffect(() => {
    return () => {
      for (const item of pendingUploadsRef.current) {
        URL.revokeObjectURL(item.objectUrl);
      }
    };
  }, []);

  const createImagesBulk = useCreateProductImagesBulk(productId);
  const deleteImage = useDeleteProductImage(productId);
  const bulkDeleteImages = useBulkDeleteProductImages(productId);
  const reorderImages = useReorderProductImages(productId);

  const toggleOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) setSelectedIds(new Set(images.map((img) => img.id)));
      else setSelectedIds(new Set());
    },
    [images],
  );

  const allSelected = images.length > 0 && selectedIds.size === images.length;
  const someSelected = selectedIds.size > 0;
  const headerChecked = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;

  const handleFilesSelected = useCallback((files: File[]) => {
    const items: PendingUploadItem[] = files.map((file) => ({
      file,
      status: "pending" as const,
      objectUrl: URL.createObjectURL(file),
    }));
    setPendingUploads(items);
  }, []);

  const handleFileProgress = useCallback(
    (index: number, status: FileUploadItemStatus, key?: string) => {
      setPendingUploads((prev) => {
        const next = [...prev];
        const item = next[index];
        if (!item) return prev;
        next[index] = { ...item, status, key };
        return next;
      });
    },
    [],
  );

  const clearPendingAndRevoke = useCallback(() => {
    setPendingUploads((prev) => {
      for (const item of prev) URL.revokeObjectURL(item.objectUrl);
      return [];
    });
  }, []);

  const handleUploadSuccess = (fileKey: string, blurhash?: string | null) => {
    setPendingUploads((prev) =>
      prev.map((p, i) => (i === 0 ? { ...p, status: "saving" as const } : p)),
    );
    createImagesBulk.mutate(
      {
        fileKeys: [fileKey],
        blurhashes: blurhash ? [blurhash] : undefined,
      },
      { onSuccess: clearPendingAndRevoke },
    );
  };

  const handleUploadSuccessMultiple = (keys: string[], blurhashes: string[]) => {
    if (keys.length === 0) return;
    setPendingUploads((prev) =>
      prev.map((p) => ({ ...p, status: "saving" as const })),
    );
    createImagesBulk.mutate(
      { fileKeys: keys, blurhashes },
      { onSuccess: clearPendingAndRevoke },
    );
  };

  const handleDelete = (image: ProductImageDTO) => {
    setImageToDelete(image);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (imageToDelete) {
      deleteImage.mutate(imageToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setImageToDelete(null);
        },
      });
    }
  };

  const confirmBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkDeleteImages.mutate(ids, {
      onSuccess: () => {
        setBulkDeleteOpen(false);
        setSelectedIds(new Set());
      },
    });
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number,
  ) => {
    e.preventDefault();
    const dragIndex = Number.parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex === dropIndex) return;

    // Reorder images
    const newImages = [...images];
    const [draggedImage] = newImages.splice(dragIndex, 1);
    if (!draggedImage) return;

    newImages.splice(dropIndex, 0, draggedImage);

    // Update sortOrder
    const updates = newImages.map((img, idx) => ({
      id: img.id,
      sortOrder: idx,
    }));

    reorderImages.mutate({ images: updates });
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 font-semibold text-lg">ຮູບພາບ</h3>
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">ຄັງຮູບພາບ</h3>
            <p className="text-muted-foreground text-sm">ຮູບທຳອິດຈະໃຊ້ເປັນປົກສິນຄ້າ</p>
          </div>
          <div className="flex items-center gap-2">
            {someSelected && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <TrashIcon className="size-4" />
                ລຶບທີ່ເລືອກ ({selectedIds.size})
              </Button>
            )}
            <PresignedImageUploadTrigger
              keyPrefix="uploads/products"
              onFilesSelected={handleFilesSelected}
              onFileProgress={handleFileProgress}
              onSuccess={handleUploadSuccess}
              onSuccessMultiple={handleUploadSuccessMultiple}
              variant="button"
              label="ອັບໂຫຼດຮູບ"
              multiple
              isSaving={createImagesBulk.isPending}
            />
          </div>
        </div>

        {images.length === 0 && pendingUploads.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg">
            <PresignedImageUploadTrigger
              keyPrefix="uploads/products"
              onFilesSelected={handleFilesSelected}
              onFileProgress={handleFileProgress}
              onSuccess={handleUploadSuccess}
              onSuccessMultiple={handleUploadSuccessMultiple}
              variant="tile"
              label="ເພີ່ມຮູບສິນຄ້າ"
              className="max-w-[240px]"
              multiple
              isSaving={createImagesBulk.isPending}
            />
          </div>
        ) : images.length === 0 && pendingUploads.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {pendingUploads.map((item) => (
              <div
                key={item.objectUrl}
                className="relative overflow-hidden rounded-lg border-2 border-muted-foreground/30 border-dashed bg-muted/10"
              >
                <div className="aspect-square bg-muted">
                  <img
                    src={item.objectUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  {item.status === "pending" && (
                    <span className="text-white text-xs">ກຳລັງຮອງ...</span>
                  )}
                  {item.status === "uploading" && (
                    <Loader2 className="size-8 animate-spin text-white" />
                  )}
                  {item.status === "done" && (
                    <CheckCircle2Icon className="size-8 text-green-400" />
                  )}
                  {item.status === "saving" && (
                    <div className="flex flex-col items-center gap-1">
                      <Loader2 className="size-8 animate-spin text-white" />
                      <span className="text-white text-xs">ກຳລັງບັນທຶກ...</span>
                    </div>
                  )}
                  {item.status === "error" && (
                    <XCircleIcon className="size-8 text-destructive" />
                  )}
                </div>
              </div>
            ))}
            <PresignedImageUploadTrigger
              keyPrefix="uploads/products"
              onFilesSelected={handleFilesSelected}
              onFileProgress={handleFileProgress}
              onSuccess={handleUploadSuccess}
              onSuccessMultiple={handleUploadSuccessMultiple}
              variant="tile"
              label="ເພີ່ມຮູບ"
              multiple
              isSaving={createImagesBulk.isPending}
            />
          </div>
        ) : (
          <div className="space-y-3">
            {images.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={headerChecked}
                  onCheckedChange={(val) =>
                    toggleAll(val === true || val === "indeterminate")
                  }
                  aria-label="ເລືອກທັງໝົດ"
                />
                <span className="text-muted-foreground text-sm">
                  {someSelected ? `ເລືອກ ${selectedIds.size} ຮູບ` : "ເລືອກທັງໝົດ"}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
              {images.map((image, index) => (
                // biome-ignore lint/a11y/noStaticElementInteractions: draggable card uses div for drag-and-drop
                <div
                  key={image.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`group relative cursor-move overflow-hidden rounded-lg border-2 transition-all hover:shadow-lg ${
                    index === 0
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-border"
                  }`}
                >
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox
                      checked={selectedIds.has(image.id)}
                      onCheckedChange={(val) =>
                        toggleOne(image.id, val === true)
                      }
                      aria-label={`ເລືອກຮູບ ${index + 1}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="aspect-square bg-muted">
                    <img
                      src={resolveImageSrc(image.fileKey) ?? ""}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                  {index === 0 && (
                    <div className="absolute top-2 left-10 rounded bg-blue-500 px-2 py-1 font-medium text-white text-xs">
                      ປົກ
                    </div>
                  )}
                  <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(image)}
                    >
                      <TrashIcon className="size-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {/* Pending upload previews */}
              {pendingUploads.map((item) => (
                <div
                  key={item.objectUrl}
                  className="relative overflow-hidden rounded-lg border-2 border-muted-foreground/30 border-dashed bg-muted/10"
                >
                  <div className="aspect-square bg-muted">
                    <img
                      src={item.objectUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    {item.status === "pending" && (
                      <span className="text-white text-xs">ກຳລັງຮອງ...</span>
                    )}
                    {item.status === "uploading" && (
                      <Loader2 className="size-8 animate-spin text-white" />
                    )}
                    {item.status === "done" && (
                      <CheckCircle2Icon className="size-8 text-green-400" />
                    )}
                    {item.status === "saving" && (
                      <div className="flex flex-col items-center gap-1">
                        <Loader2 className="size-8 animate-spin text-white" />
                        <span className="text-white text-xs">ກຳລັງບັນທຶກ...</span>
                      </div>
                    )}
                    {item.status === "error" && (
                      <XCircleIcon className="size-8 text-destructive" />
                    )}
                  </div>
                </div>
              ))}
              {/* Add Image Slot */}
              <PresignedImageUploadTrigger
                keyPrefix="uploads/products"
                onFilesSelected={handleFilesSelected}
                onFileProgress={handleFileProgress}
                onSuccess={handleUploadSuccess}
                onSuccessMultiple={handleUploadSuccessMultiple}
                variant="tile"
                multiple
                isSaving={createImagesBulk.isPending}
              />
            </div>
          </div>
        )}
      </div>

      <Modal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບຮູບ</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຮູບນີ້?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteImage.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteImage.isPending}
            >
              {deleteImage.isPending ? "ກຳລັງລຶບ..." : "ລຶບ"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບຫຼາຍຮູບ</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ {selectedIds.size} ຮູບທີ່ເລືອກ?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={bulkDeleteImages.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBulkDelete}
              disabled={bulkDeleteImages.isPending}
            >
              {bulkDeleteImages.isPending ? "ກຳລັງລຶບ..." : "ລຶບ"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
